import { Trans } from '@lingui/react/macro';
import { FileText, Plus, SearchX } from 'lucide-react';

import { Button } from '@/components/ui/button';

/**
 * Empty-state banner for the templates list (L17). Two variants:
 *
 * - default (`filtered` false) — the realm has **no** templates: a
 *   "no templates yet" prompt with a Create CTA (`onCreateNew`).
 * - filtered (`filtered` true) — templates exist but the active
 *   search/filter excludes them all: a "no templates found" message
 *   with an optional Clear-filters action (`onClearFilters`).
 */
export function TemplatesEmptyState({
  onCreateNew,
  filtered = false,
  onClearFilters,
}: TemplatesEmptyStateProps) {
  if (filtered) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-12">
        <SearchX className="h-10 w-10 text-muted-foreground" />
        <h3 className="text-[length:var(--section-title-font-size)] leading-[var(--section-title-line-height)] font-semibold">
          <Trans>No templates found</Trans>
        </h3>
        <p className="text-[length:var(--body-font-size)] leading-[var(--body-line-height)] text-[var(--muted-foreground)]">
          <Trans>No templates match your search or filters. Try adjusting them.</Trans>
        </p>
        {onClearFilters && (
          <Button variant="outline" onClick={onClearFilters}>
            <Trans>Clear filters</Trans>
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-12">
      <FileText className="h-10 w-10 text-muted-foreground" />
      <h3 className="text-[length:var(--section-title-font-size)] leading-[var(--section-title-line-height)] font-semibold">
        <Trans>No templates yet</Trans>
      </h3>
      <p className="text-[length:var(--body-font-size)] leading-[var(--body-line-height)] text-[var(--muted-foreground)]">
        <Trans>Get started by creating your first rota template.</Trans>
      </p>
      <Button onClick={onCreateNew}>
        <Plus className="mr-2 h-4 w-4" /> <Trans>Create template</Trans>
      </Button>
    </div>
  );
}

interface TemplatesEmptyStateProps {
  onCreateNew: () => void;
  /** True when templates exist but the active search/filter excludes them all. */
  filtered?: boolean;
  /** Clears the active filters; surfaced as a button in the filtered variant. */
  onClearFilters?: () => void;
}
